/**
 * The three ledgers: fixed costs, held funds, tax filings.
 *
 * Every money change goes through a confirm dialog that takes a reason, because the audit
 * row needs one and because a ledger edit with no stated reason is unreadable six months
 * later. Nothing here is optimistic: the write waits for the server and renders what came
 * back.
 *
 * Mark filed is the action that matters most. A Michigan return that nobody filed is the
 * single most expensive thing on this page, so the overdue filing carries a critical pill,
 * its own days-overdue count, and a one click path to recording the confirmation number.
 */
import { useState } from 'react';
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  MoneyText,
  Pill,
  Skeleton,
  TextInput,
  type Column,
} from '@/components';
import { formatMoney } from '@/lib/format';
import {
  useFixedCosts,
  useHeldFunds,
  useMarkTaxFiled,
  useRecomputeTaxFiling,
  useTaxFilings,
} from './api';
import type { FixedCostRow, HeldFundRow, TaxFilingRow } from './types';
import './finance.css';

export function LedgersPage() {
  return (
    <div className="o-stack">
      <TaxFilingsCard />
      <FixedCostsCard />
      <HeldFundsCard />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tax filings                                                         */
/* ------------------------------------------------------------------ */

function statusTone(row: TaxFilingRow): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (row.status === 'paid' || row.status === 'filed') return 'ok';
  if (row.overdue || row.status === 'late') return 'crit';
  if (row.days_until_due !== null && row.days_until_due <= 10) return 'warn';
  return 'neutral';
}

export function TaxFilingsCard() {
  const query = useTaxFilings();
  const markFiled = useMarkTaxFiled();
  const recompute = useRecomputeTaxFiling();
  const [filing, setFiling] = useState<TaxFilingRow | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [remitted, setRemitted] = useState('');

  const rows = query.data?.items ?? [];

  const columns: readonly Column<TaxFilingRow>[] = [
    { id: 'period', header: 'Period', priority: 1, mono: true, render: (r) => `${r.jurisdiction} ${r.period_start} to ${r.period_end}` },
    { id: 'due', header: 'Due', priority: 1, mono: true, render: (r) => r.due_date },
    {
      id: 'status',
      header: 'Status',
      priority: 1,
      render: (r) => (
        <Pill tone={statusTone(r)}>
          {r.overdue && r.status !== 'filed' && r.status !== 'paid'
            ? `overdue ${Math.abs(r.days_until_due ?? 0)}d`
            : r.status}
        </Pill>
      ),
    },
    {
      id: 'collected',
      header: 'Collected',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.collected_cents} showCents />,
    },
    {
      id: 'owed',
      header: 'Owed',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.owed_cents} showCents />,
    },
    { id: 'confirmation', header: 'Confirmation', priority: 4, mono: true, render: (r) => r.confirmation_number ?? '' },
    {
      id: 'actions',
      header: '',
      priority: 2,
      render: (r) =>
        r.status === 'filed' || r.status === 'paid' ? (
          <span className="o-muted">filed {r.filed_date}</span>
        ) : (
          <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              size="sm"
              onClick={() => {
                setFiling(r);
                setRemitted(String(r.owed_cents));
                setConfirmation('');
              }}
            >
              Mark filed
            </Button>
            <Button
              size="sm"
              variant="ghost"
              busy={recompute.isPending}
              onClick={() => recompute.mutate({ id: r.tax_filing_id, reason: 'recompute from paid orders' })}
            >
              Recompute
            </Button>
          </span>
        ),
    },
  ];

  if (query.isError) {
    return (
      <ErrorState
        message="Tax filings could not be loaded."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <Card
      title="Tax filings"
      basis="ledger"
      action={
        rows.some((r) => r.overdue) ? <Pill tone="crit">a filing is overdue</Pill> : null
      }
    >
      {query.isLoading ? (
        <Skeleton shape="row" lines={3} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.tax_filing_id}
          caption="Sales tax filing periods"
          emptyState={<EmptyState message="No filing periods yet. The nightly job creates the current one." />}
        />
      )}
      <p className="o-muted">
        Michigan is a single statewide rate and the return is due on the 20th of the month after the
        period ends, rolled forward off weekends and federal holidays. Collected recomputes from
        paid orders by ship-to state until the period is locked.
      </p>

      <ConfirmDialog
        open={filing !== null}
        title={filing ? `Mark ${filing.jurisdiction} ${filing.period_start} filed?` : ''}
        body="This records the filing against the period. It does not file anything with the state."
        effects={[
          'Sets the filed date and the remitted amount on the ledger row',
          'Moves the period out of the overdue alert',
          'Writes an audit row with the before and after values',
        ]}
        confirmLabel="Record filing"
        reasonRequired
        busy={markFiled.isPending}
        extra={
          <>
            <TextInput
              label="Amount remitted, in cents"
              mono
              value={remitted}
              onChange={(event) => setRemitted(event.currentTarget.value)}
              hint={filing ? `Owed on this period: ${formatMoney(filing.owed_cents)}` : undefined}
            />
            <TextInput
              label="Confirmation number"
              mono
              value={confirmation}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
            />
          </>
        }
        onCancel={() => setFiling(null)}
        onConfirm={async ({ reason }) => {
          if (!filing) return;
          await markFiled.mutateAsync({
            id: filing.tax_filing_id,
            filed_date: new Date().toISOString().slice(0, 10),
            remitted_cents: Number(remitted) || 0,
            confirmation_number: confirmation.trim() === '' ? null : confirmation.trim(),
            paid_date: null,
            reason: reason ?? 'filed',
          });
          setFiling(null);
        }}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Fixed costs                                                         */
/* ------------------------------------------------------------------ */

export function FixedCostsCard() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const query = useFixedCosts(includeInactive);
  const rows = query.data?.items ?? [];

  const columns: readonly Column<FixedCostRow>[] = [
    { id: 'name', header: 'Name', priority: 1, render: (r) => r.name },
    { id: 'category', header: 'Category', priority: 3, render: (r) => r.category.replace(/_/g, ' ') },
    {
      id: 'amount',
      header: 'Amount',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.amount_cents} showCents />,
    },
    { id: 'frequency', header: 'Frequency', priority: 2, render: (r) => r.frequency.replace(/_/g, ' ') },
    {
      id: 'monthly',
      header: 'Monthly equivalent',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.monthly_equivalent_cents} showCents={false} />,
    },
    { id: 'effective', header: 'Effective', priority: 4, mono: true, render: (r) => `${r.start_date}${r.end_date ? ` to ${r.end_date}` : ''}` },
    {
      id: 'flags',
      header: 'In',
      priority: 5,
      render: (r) => (
        <span style={{ display: 'flex', gap: 'var(--space-1)' }}>
          {r.include_in_pnl ? <Pill tone="neutral">P and L</Pill> : null}
          {r.include_in_cash_forecast ? <Pill tone="neutral">cash</Pill> : null}
        </span>
      ),
    },
  ];

  return (
    <Card
      title="Fixed costs"
      basis="ledger"
      action={
        <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <Pill tone="neutral">
            monthly nut {formatMoney(query.data?.monthly_nut_cents ?? null, { showCents: false })}
          </Pill>
          <Button size="sm" variant="ghost" onClick={() => setIncludeInactive((v) => !v)}>
            {includeInactive ? 'Active only' : 'Show closed versions'}
          </Button>
        </span>
      }
    >
      {query.isLoading ? (
        <Skeleton shape="row" lines={4} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.fixed_cost_id}
          caption="Recurring fixed costs"
          emptyState={<EmptyState message="No fixed costs recorded." />}
        />
      )}
      <p className="o-muted">
        An edit to an amount or a schedule creates a new effective-dated version rather than
        changing this one, so replaying a past window keeps using the amount that was in force
        then. Owner draws and debt principal are excluded from the profit and loss and still appear
        in the cash forecast.
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Held funds                                                          */
/* ------------------------------------------------------------------ */

export function HeldFundsCard() {
  const query = useHeldFunds();
  const rows = query.data?.items ?? [];
  const outstanding = rows.reduce((sum, r) => sum + r.outstanding_cents, 0);
  const pastDue = rows.filter((r) => r.days_past_expected > 0);

  const columns: readonly Column<HeldFundRow>[] = [
    { id: 'held_date', header: 'Held', priority: 1, mono: true, render: (r) => r.held_date },
    { id: 'reference', header: 'Reference', priority: 3, mono: true, render: (r) => r.reference ?? r.payout_id ?? '' },
    {
      id: 'amount',
      header: 'Held',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.amount_held_cents} showCents />,
    },
    {
      id: 'released',
      header: 'Released',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.released_cents} showCents />,
    },
    {
      id: 'outstanding',
      header: 'Outstanding',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.outstanding_cents} showCents />,
    },
    {
      id: 'expected',
      header: 'Expected release',
      priority: 2,
      mono: true,
      render: (r) =>
        r.expected_release_date === null ? (
          <span className="o-muted">not set</span>
        ) : r.days_past_expected > 0 ? (
          <Pill tone={r.days_past_expected >= 15 ? 'crit' : 'warn'}>{`${r.days_past_expected}d past`}</Pill>
        ) : (
          r.expected_release_date
        ),
    },
    { id: 'status', header: 'Status', priority: 2, render: (r) => <Pill tone={r.status === 'released' ? 'ok' : 'neutral'}>{r.status.replace(/_/g, ' ')}</Pill> },
  ];

  return (
    <Card
      title="Held funds"
      basis="ledger"
      action={
        <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Pill tone="neutral">outstanding {formatMoney(outstanding, { showCents: false })}</Pill>
          {pastDue.length > 0 ? <Pill tone="warn">{`${pastDue.length} past expected release`}</Pill> : null}
        </span>
      }
    >
      {query.isLoading ? (
        <Skeleton shape="row" lines={3} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.held_fund_id}
          caption="Processor reserve held and released"
          emptyState={<EmptyState message="No reserve rows. A payout import creates them." />}
        />
      )}
      <p className="o-muted">
        The reserve is a receivable, not an expense, and it is not the gateway receivable. Releases
        are applied against open rows oldest first, which is the order the statements themselves
        use.
      </p>
    </Card>
  );
}
